import { computed, getCurrentInstance, ref, toRef, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import type { RawLocation } from 'vue-router';
import { useOverallAssessmentStore } from '../stores/overallAssessment';
import {
  ASSESSMENT_MODE_ROUTE_LOCATIONS,
} from '@/features/assessment/config/assessment-mode.routes';
import { useGuidedModeStore } from '@/features/guided-mode/stores/guidedMode';
import { useEvidenceStore } from '@/features/text-mode/stores/evidence.store';
import { useNoteStore } from '@/features/text-mode/stores/note.store';
import { usePatientStore } from '@/features/text-mode/stores/patient.store';
import { useQuestionStore } from '@/features/text-mode/stores/question.store';
import { buildEncounterDetails } from '@/utils/encounterDetails';
import { resetAssessmentState, resetEncounter } from '@/utils/encounterLifecycle';
import { sendCollect } from '@/utils/collectUpload';
import { getResponse } from '@/utils/panelAccess';
import {
  clearActiveTextModeContext,
  getActiveTextModeContext,
  trackFinalizeSaveClicked,
} from '@/features/text-mode/analytics/text-mode-analytics';
import {
  resolveAssessmentMode,
  updateActiveTextModeContext,
} from '@/features/text-mode/analytics/text-mode-context';
import type { EvidenceCategory } from '@/features/text-mode/types/text-mode.types';
import type {
  AssessedCondition,
  AssessmentTab,
  JourneyStage,
  PatientInfo,
} from '../types/overall-assessment.types';
import {
  PVI_ENCOUNTER_MODES,
  savePatientVisitInsights,
} from '@/utils/patientVisitInsightsUtils';
import {
  buildPatientInfo,
  buildReviewEvidenceCategories,
  UNKNOWN_TOKEN,
} from '../utils/review-data.utils';
import { useCollectStore } from '@/stores/collect';
import { useEncounterStore } from '@/stores/encounter';
import { usePartnerConfigStore } from '@/stores/partnerConfig';
import { useMrxStore } from '@/stores/mrx';
import { useSavedEncountersStore } from '@/stores/savedEncounters';
import { useTrackStore } from '@/stores/track';
import { conditions as mrxConditions } from '@/utils/mrxEngine';
import {
  resolveEncounterCopyIdentifiers,
  type CopyIdentifier,
} from '@/utils/encounterCopyUtils';

const HOME_ROUTE = '/start';

export interface OverallAssessmentWorkflow {
  activeTab: Ref<AssessmentTab>;
  stage: Ref<JourneyStage>;
  carePlanRenderKey: Ref<number>;
  hasVisitedCarePlan: Ref<boolean>;
  showReviewData: Ref<boolean>;
  showFinalizeDialog: Ref<boolean>;
  showDiscardDialog: Ref<boolean>;
  isSaving: Ref<boolean>;
  saveFailed: Ref<boolean>;
  autoSave: ComputedRef<boolean>;
  patientInfo: ComputedRef<PatientInfo>;
  reviewEvidenceCategories: ComputedRef<EvidenceCategory[]>;
  copyIdentifiers: ComputedRef<CopyIdentifier[]>;
  hasNoRisk: ComputedRef<boolean>;
  assessmentReady: ComputedRef<boolean>;
  onTabChange: (tab: AssessmentTab) => void;
  handleNextFromRiskAssessment: () => void;
  handleFinalizeSave: () => void;
  handleReferralAnswer: (answer: 'yes' | 'no') => void;
  handleReferralDismiss: () => void;
  handleSaveResults: () => Promise<void>;
  handleDiscardRequest: () => void;
  handleDiscardDecision: (confirmed: boolean) => Promise<void>;
  handleNewAssessment: () => Promise<void>;
  handleReturnHome: () => Promise<void>;
  clearWorkflowContext: () => void;
}

type VueProxy = {
  $router?: { push: (path: RawLocation) => Promise<unknown> };
  $t: (token: string) => string;
  $n: (value: number, formatKey?: string) => string;
  $track: (event: string, payload?: Record<string, unknown>) => void;
  $analyticsEvents: Record<string, string>;
  $sentry?: {
    withScope: (callback: (scope: { setTag: (key: string, value: string) => void }) => void) => void;
    captureException: (error: unknown) => void;
  };
};

export function useOverallAssessmentWorkflow(): OverallAssessmentWorkflow {
  const vm = getCurrentInstance();
  const proxy = vm?.proxy as unknown as VueProxy | undefined;

  const evidenceStore = useEvidenceStore();
  const patientStore = usePatientStore();
  const questionStore = useQuestionStore();
  const noteStore = useNoteStore();
  const mrxStore = useMrxStore();
  const trackStore = useTrackStore();
  const partnerConfigStore = usePartnerConfigStore();
  const guidedModeStore = useGuidedModeStore();
  const overallAssessmentStore = useOverallAssessmentStore();

  const activeTab = toRef(overallAssessmentStore, 'activeTab');
  const stage = toRef(overallAssessmentStore, 'stage');
  const carePlanRenderKey = ref(0);
  const hasVisitedCarePlan = toRef(overallAssessmentStore, 'hasVisitedCarePlan');
  const showReviewData = ref(false);
  const showFinalizeDialog = ref(false);
  const showDiscardDialog = ref(false);
  const isSaving = ref(false);
  const saveFailed = ref(false);
  const autoSave = computed(() => partnerConfigStore.flexiAutoSave);

  const analyticsPanelName = computed<string>(() => {
    if (stage.value === 'finalize') return 'overall-assessment/finalize';
    if (stage.value === 'complete') return 'overall-assessment/complete';
    return activeTab.value === 'Care Plan'
      ? 'overall-assessment/care-plan'
      : 'overall-assessment/risk';
  });

  watch(analyticsPanelName, (panelName) => {
    updateActiveTextModeContext({ panelName });
  }, { immediate: true });

  const unknownLabel = computed(() => String(proxy?.$t(UNKNOWN_TOKEN) ?? UNKNOWN_TOKEN));

  const patientInfo = computed<PatientInfo>(() => {
    const track = trackStore.current;
    const patientId = (patientStore.patient?.id ?? getResponse(track, 'patient_id', 'number')?.value) as
      string | number | null | undefined;

    return buildPatientInfo({
      evidenceCategories: evidenceStore.categories,
      patientId,
      ageInDays: getResponse(track, 'age', 'days')?.value as string | number | undefined,
      isMaleSelected: Boolean(getResponse(track, 'gender', 'male')?.selected),
      isFemaleSelected: Boolean(getResponse(track, 'gender', 'female')?.selected),
      unknownLabel: unknownLabel.value,
      translate: (token: string) => String(proxy?.$t(token) ?? token),
      formatNumber: (value: number) => String(proxy?.$n(value, 'decimal') ?? value),
    });
  });

  const reviewEvidenceCategories = computed<EvidenceCategory[]>(() => {
    const patientId = patientInfo.value.id === unknownLabel.value ? null : patientInfo.value.id;

    return buildReviewEvidenceCategories(
      evidenceStore.categories,
      questionStore.questions,
      patientId,
    );
  });

  const copyIdentifiers = computed<CopyIdentifier[]>(() => {
    const track = trackStore.current;
    const patientId = (patientStore.patient?.id ?? getResponse(track, 'patient_id', 'number')?.value) as
      string | number | null | undefined;
    const mode = getActiveTextModeContext()?.mode === 'guided' ? 'guided' : 'text';

    return resolveEncounterCopyIdentifiers({
      mode,
      patientId,
      configuredPanel: partnerConfigStore.demographicsIdentifier?.panel,
      assessmentDefinitions: guidedModeStore.clinicalContent.assessmentDefinitions,
      translate: token => String(proxy?.$t(token) ?? token),
    });
  });

  const hasNoRisk = computed<boolean>(() => {
    const assessed = ((mrxStore.assessments() as AssessedCondition[]) || [])
      .filter((assessment) => assessment.severity !== 'not_assessed');

    return assessed.length > 0
      && assessed.every((assessment) => assessment.triage_recommendation === 'none');
  });

  const assessmentReady = computed<boolean>(() => mrxStore.valid === true);

  const onTabChange = (tab: AssessmentTab): void => {
    activeTab.value = tab;

    if (tab === 'Care Plan') {
      proxy?.$track(proxy?.$analyticsEvents.CARE_PLAN_VIEWED, {
        component: 'OverallAssessment',
        panel_name: 'overall-assessment/care-plan',
        first_visit: !hasVisitedCarePlan.value,
      });
      hasVisitedCarePlan.value = true;
      carePlanRenderKey.value += 1;
    }
  };

  const handleNextFromRiskAssessment = (): void => {
    if (hasNoRisk.value) {
      handleFinalizeSave();
      return;
    }

    onTabChange('Care Plan');
  };

  const savePatientReferredAnswer = (answer: 'yes' | 'no'): void => {
    const track = trackStore.current as {
      panels?: Array<{
        name?: string;
        responses?: Array<{ value?: string; selected?: boolean }>;
      }>;
    } | undefined;
    const panel = track?.panels?.find((candidate) => candidate.name === 'encounter_patient_referred');

    if (!panel?.responses) return;

    const responses = panel.responses.map((response) => ({
      ...response,
      selected: response.value === answer,
    }));

    trackStore.setPanelResponses(panel, responses);
  };

  const resetStores = (): void => {
    resetAssessmentState();
  };

  const reportPviSaveFailure = (error: unknown): void => {
    console.error('Failed to save Flexi encounter to Patient Visits & Insights:', error);

    proxy?.$sentry?.withScope((scope) => {
      scope.setTag('location', 'pvi-finalize-save');
      proxy.$sentry?.captureException(error);
    });
  };

  type PviEncounterMode = (typeof PVI_ENCOUNTER_MODES)[keyof typeof PVI_ENCOUNTER_MODES];

  const enqueueEncounterPviSave = (
    encounterId: string,
    encounterMode: PviEncounterMode,
    textModeStartedAt?: string,
  ): void => {
    if (!encounterId) return;

    const bundle = mrxStore.bundleSnapshot;
    const conditions = Array.isArray(bundle)
      ? bundle.filter((item) => item?.type === 'assessment' && item?.severity !== 'not_assessed')
      : [];

    if (conditions.length === 0) return;

    const activeTrack = trackStore.current as { path?: string } | undefined;
    const currentTrack = { path: activeTrack?.path };
    const sessionDetails = buildEncounterDetails();
    const textModeStart = textModeStartedAt ? new Date(textModeStartedAt) : null;
    const hasValidTextModeStart = !!textModeStart && !Number.isNaN(textModeStart.getTime());
    const pviSessionDetails = {
      ...sessionDetails,
      timing: {
        ...(sessionDetails.timing || {}),
        // pausedDurationMs is injected here for PVI duration calculation only.
        // It is intentionally absent from buildEncounterDetails() to keep the
        // partner payload indistinguishable from a non-paused encounter.
        pausedDurationMs: useEncounterStore().pausedDurationMs,
        ...(hasValidTextModeStart ? { start: textModeStart.toISOString() } : {}),
        end: new Date().toISOString(),
      },
    };
    const selectedTriage = mrxStore.triageSelected();
    const triage = selectedTriage === 'none_selected'
      ? mrxStore.triage
      : selectedTriage;
    const selectedConditions = conditions
      .filter((condition) => condition.selected)
      .map((condition) => condition.name);

    void Promise.resolve()
      .then(() => mrxConditions())
      .then((categoriesData) => savePatientVisitInsights({
        conditions,
        categoriesData: Array.isArray(categoriesData) ? categoriesData : [],
        currentTrack,
        sessionDetails: () => pviSessionDetails,
        selectedTriage: triage,
        selectedConditions,
        encounterId,
        encounterMode,
      }))
      .catch(reportPviSaveFailure);
  };

  const handleFinalizeSave = (): void => {
    if (!hasVisitedCarePlan.value && !hasNoRisk.value) return;

    const activeTextModeContext = getActiveTextModeContext();
    if (activeTextModeContext) {
      trackFinalizeSaveClicked(activeTextModeContext);
    }

    showFinalizeDialog.value = true;
  };

  const handleReferralAnswer = (answer: 'yes' | 'no'): void => {
    showFinalizeDialog.value = false;
    savePatientReferredAnswer(answer);
    stage.value = 'finalize';
    if (autoSave.value) {
      // Queue through the same collection/reporting path as a manual save.
      // On failure stay on finalize so the worker can retry without losing data.
      void handleSaveResults();
    }
  };

  const handleReferralDismiss = (): void => {
    showFinalizeDialog.value = false;
  };

  const leaveJourney = async (path: string): Promise<void> => {
    resetStores();
    clearActiveTextModeContext();
    await proxy?.$router?.push(path);
  };

  const handleSaveResults = async (): Promise<void> => {
    if (isSaving.value) return;
    isSaving.value = true;
    saveFailed.value = false;

    const collectStore = useCollectStore();
    const activeTextModeContext = getActiveTextModeContext();
    const startedAt = Date.now();

    try {
      proxy?.$track(proxy?.$analyticsEvents.ENCOUNTER_SAVE_STARTED ?? 'ENCOUNTER_SAVE_STARTED', {
        component: 'Upload',
        is_auto_collect: autoSave.value || partnerConfigStore.autoCollectTracks,
        ...(activeTextModeContext?.encounterId ? { encounter_id: activeTextModeContext.encounterId } : {}),
      });

      const collection = await sendCollect({
        clinicalNoteText: noteStore.clinicalNote.text,
        encounterId: activeTextModeContext?.encounterId,
      });
      if (!collection.ok) {
        saveFailed.value = true;
        return;
      }

      if (activeTextModeContext?.encounterId) {
        enqueueEncounterPviSave(
          activeTextModeContext.encounterId,
          activeTextModeContext.mode === 'guided'
            ? PVI_ENCOUNTER_MODES.FLEXI_GUIDED
            : PVI_ENCOUNTER_MODES.FLEXI_TEXT,
          activeTextModeContext.textModeStartedAt,
        );
      }

      const completedUuid = useEncounterStore().uuid;
      if (completedUuid) {
        useSavedEncountersStore().delete(completedUuid);
      }

      collectStore.incrementRecordsInQueue();

      proxy?.$track(proxy?.$analyticsEvents.ENCOUNTER_SAVED ?? 'ENCOUNTER_SAVED', {
        component: 'Upload',
        is_auto_collect: autoSave.value || partnerConfigStore.autoCollectTracks,
        save_duration_ms: Date.now() - startedAt,
        records_in_queue: collectStore.recordsInQueue,
      });

      stage.value = 'complete';
    } catch (error) {
      saveFailed.value = true;
      proxy?.$sentry?.captureException(error);
    } finally {
      isSaving.value = false;
    }
  };

  const handleDiscardRequest = (): void => {
    showDiscardDialog.value = true;
  };

  const handleDiscardDecision = async (confirmed: boolean): Promise<void> => {
    showDiscardDialog.value = false;

    if (!confirmed) {
      proxy?.$track(
        proxy?.$analyticsEvents.ENCOUNTER_DISCARD_CANCELLED ?? 'ENCOUNTER_DISCARD_CANCELLED',
        { component: 'Upload' },
      );
      return;
    }

    const activeTextModeContext = getActiveTextModeContext();
    proxy?.$track(proxy?.$analyticsEvents.ENCOUNTER_DISCARDED ?? 'ENCOUNTER_DISCARDED', {
      component: 'Upload',
      source: 'finalize',
      ...(activeTextModeContext?.encounterId ? { encounter_id: activeTextModeContext.encounterId } : {}),
    });

    await leaveJourney(HOME_ROUTE);
  };

  const handleNewAssessment = async (): Promise<void> => {
    const mode = resolveAssessmentMode(getActiveTextModeContext());
    // Reset and navigate in the same turn so this page cannot remount its risk assessment.
    void resetEncounter();
    // begin assigns the new identity before waiting for geolocation, as on Start.
    void useEncounterStore().begin();
    await proxy?.$router?.push(ASSESSMENT_MODE_ROUTE_LOCATIONS[mode]);
  };

  const handleReturnHome = async (): Promise<void> => {
    await leaveJourney(HOME_ROUTE);
  };

  const clearWorkflowContext = (): void => {
    clearActiveTextModeContext();
  };

  return {
    activeTab,
    stage,
    carePlanRenderKey,
    hasVisitedCarePlan,
    showReviewData,
    showFinalizeDialog,
    showDiscardDialog,
    isSaving,
    saveFailed,
    autoSave,
    patientInfo,
    reviewEvidenceCategories,
    copyIdentifiers,
    hasNoRisk,
    assessmentReady,
    onTabChange,
    handleNextFromRiskAssessment,
    handleFinalizeSave,
    handleReferralAnswer,
    handleReferralDismiss,
    handleSaveResults,
    handleDiscardRequest,
    handleDiscardDecision,
    handleNewAssessment,
    handleReturnHome,
    clearWorkflowContext,
  };
}
